/**
 * The proposal's look, from the business's two colours.
 *
 * One function for the builder's canvas, the customer's page at `/p/:token`
 * and the PDF, so the three cannot drift. It takes whatever Settings holds —
 * possibly nothing — and returns a full palette: a colour that is safe for
 * small text on white is not the same colour as the one on the logo, and a
 * lime green needs dark ink on it where a forest green needs white. Those are
 * computed here rather than chosen by whoever draws. Decision 81.
 */

/**
 * Buster Navy and Deep Navy from the brand guide, the same values as
 * `--color-brand-800` and `--color-brand-900` in `apps/web/src/styles.css`
 * (ADR 006, ADR 024). Used until the office sets its own in Settings.
 *
 * Not Buster Red for the accent: the accent fills the cover's footer band
 * behind small text, and red reaches about 4.1:1 against white or ink alike.
 * The red arrives on every document through the logo instead.
 */
export const DEFAULT_BRAND_COLOUR = '#002b5c'
export const DEFAULT_ACCENT_COLOUR = '#001a38'

export interface ProposalTheme {
  /** The logo colour: headings, rules, the chosen package. */
  brand: string
  /** The brand darkened until small text on white reads (WCAG AA, 4.5:1). */
  brandText: string
  /** A pale wash of the brand, for panels and the recommended card. */
  brandSoft: string
  /** Text drawn on a `brand` fill: white or near-black, whichever reads. */
  onBrand: string
  /** The second colour: the letterhead's small print, secondary headings. */
  accent: string
  accentText: string
  accentSoft: string
  onAccent: string
}

export function proposalTheme(input: {
  brandColour?: string | null
  accentColour?: string | null
}): ProposalTheme {
  const brand = hexOr(input.brandColour, DEFAULT_BRAND_COLOUR)
  const accent = hexOr(input.accentColour, DEFAULT_ACCENT_COLOUR)

  return {
    brand,
    brandText: readableOnWhite(brand),
    brandSoft: mix(brand, '#ffffff', 0.88),
    onBrand: textOn(brand),
    accent,
    accentText: readableOnWhite(accent),
    accentSoft: mix(accent, '#ffffff', 0.9),
    onAccent: textOn(accent),
  }
}

/**
 * The right-hand block of the letterhead, top to bottom: address lines, then
 * email, then the ABN. Phone and website follow when there is room for them,
 * which a letterhead often leaves out but a customer may
 * want. Blank fields are left out rather than printed as empty lines.
 */
export function letterheadLines(business: {
  address?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
  taxNumber?: string | null
}): string[] {
  const lines: string[] = []
  for (const line of (business.address ?? '').split(/\r?\n/)) {
    if (line.trim() !== '') lines.push(line.trim())
  }
  if (business.phone?.trim()) lines.push(business.phone.trim())
  if (business.email?.trim()) lines.push(business.email.trim())
  if (business.website?.trim()) lines.push(business.website.trim().replace(/^https?:\/\//, ''))
  if (business.taxNumber?.trim()) lines.push(`ABN: ${business.taxNumber.trim()}`)
  return lines
}

/** What the letterhead calls the document, above its date. Provisional until the business says. */
export const PROPOSAL_HEADING = 'Pest Control Quotation'

/** "Pest Control Quotation #QTE-00003". */
export function proposalHeading(number: string): string {
  return `${PROPOSAL_HEADING} #${number}`
}

const LETTER_MONTH = new Intl.DateTimeFormat('en-AU', { month: 'long' })

/** "23rd September 2026" — the letterhead's date, as the business writes it. */
export function letterDate(value: Date | string): string {
  const date = new Date(value)
  const day = date.getDate()
  const teen = day % 100 >= 11 && day % 100 <= 13
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] ?? 'th'
  return `${day}${suffix} ${LETTER_MONTH.format(date)} ${date.getFullYear()}`
}

/* -------------------------------------------------------------------------- */

const HEX = /^#[0-9a-fA-F]{6}$/

function hexOr(value: string | null | undefined, fallback: string): string {
  return value && HEX.test(value) ? value.toLowerCase() : fallback
}

type Rgb = [number, number, number]

function rgbOf(hex: string): Rgb {
  const value = Number.parseInt(hex.slice(1), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

function hexOf([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`
}

/** `amount` of the way from `from` to `to`. */
export function mix(from: string, to: string, amount: number): string {
  const a = rgbOf(from)
  const b = rgbOf(to)
  return hexOf([0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * amount) as Rgb)
}

function luminance(hex: string): number {
  const [r, g, b] = rgbOf(hex).map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as Rgb
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrast(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number]
  return (light + 0.05) / (dark + 0.05)
}

const INK = '#111827'

function textOn(fill: string): string {
  return contrast(fill, '#ffffff') >= contrast(fill, INK) ? '#ffffff' : INK
}

/** Darkens toward black in small steps until 4.5:1 on white. */
function readableOnWhite(colour: string): string {
  let current = colour
  for (let step = 0; step < 20 && contrast(current, '#ffffff') < 4.5; step += 1) {
    current = mix(current, '#000000', 0.08)
  }
  return current
}
